import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Terminal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WIZARD_BASE_COMMAND } from '@/lib/wizardCommand';

export interface WizardInstallSnippetProps {
  variant?: 'hero' | 'page';
  showHint?: boolean;
  showFootnote?: boolean;
  showGuideLink?: boolean;
  onCopy?: () => void;
  onGuideClick?: () => void;
  className?: string;
}

async function copyCommandToClipboard(command: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(command);
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = command;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}

export default function WizardInstallSnippet({
  variant = 'page',
  showHint = variant === 'hero',
  showFootnote = variant === 'hero',
  showGuideLink = variant === 'hero',
  onCopy,
  onGuideClick,
  className = '',
}: WizardInstallSnippetProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(variant === 'hero' ? 'homepage' : 'integrations');
  const [copied, setCopied] = useState(false);

  const copyLabelKey = variant === 'hero' ? 'hero.wizard.copyCommand' : 'wizard.cta.copyCommand';
  const copiedLabelKey = variant === 'hero' ? 'hero.wizard.copied' : 'wizard.cta.copied';

  const handleCopy = async () => {
    await copyCommandToClipboard(WIZARD_BASE_COMMAND);
    setCopied(true);
    onCopy?.();
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGuideClick = () => {
    onGuideClick?.();
    navigate('/wizard');
  };

  const containerClass =
    variant === 'hero'
      ? `mx-auto max-w-xl px-4 md:px-0 ${className}`
      : `mb-6 ${className}`;

  return (
    <div className={containerClass}>
      {showHint && (
        <p className="mb-2 text-sm font-medium text-muted-foreground">
          {t('hero.wizard.hint')}
        </p>
      )}
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
        <code
          className="w-full select-all rounded-lg bg-gray-900 px-4 py-3 font-mono text-sm text-green-400 sm:w-auto md:text-base"
          aria-label={WIZARD_BASE_COMMAND}
        >
          {WIZARD_BASE_COMMAND}
        </code>
        <Button
          onClick={() => void handleCopy()}
          variant="outline"
          size="lg"
          aria-label={copied ? t(copiedLabelKey) : t(copyLabelKey)}
        >
          <Terminal className="mr-2 h-4 w-4" aria-hidden />
          {copied ? t(copiedLabelKey) : t(copyLabelKey)}
        </Button>
      </div>
      {(showFootnote || showGuideLink) && (
        <div className="mt-3 flex flex-col items-center gap-1 sm:flex-row sm:justify-center sm:gap-3">
          {showFootnote && (
            <p className="text-sm text-muted-foreground">{t('hero.wizard.footnote')}</p>
          )}
          {showFootnote && showGuideLink && (
            <span className="hidden text-muted-foreground sm:inline" aria-hidden>
              ·
            </span>
          )}
          {showGuideLink && (
            <button
              type="button"
              onClick={handleGuideClick}
              className="text-sm text-indigo-600 hover:underline"
            >
              {t('hero.wizard.guideLink')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
